export default class SelectFactory {
  constructor() {
    const selects = document.querySelectorAll('select.dd-select');

    selects.forEach((root) => {
      const config = {
        isMultiselect: root.classList.contains('multiselect'),
        isHighlightSelected: root.classList.contains('highlight-selected'),
        isBig: root.classList.contains('big'),
        hasDelete: root.classList.contains('has-delete'),
        isOpen: root.classList.contains('open'),
        isDisabled: root.classList.contains('disabled'),
      };

      new Select(root, config);
    });
  }
}

class Select {
  constructor(root, config) {
    this.root = root;
    this.config = config;
    this.isOpen = false;

    this.initOptions();

    if (this.config.isMultiselect) {
      this.initEmptyInput();
    }

    this.render();
    this.updateInputWrapper();
    this.initEvents();
  }

  render() {
    this.wrapper = document.createElement('div');
    this.inputWrapper = document.createElement('div');
    this.list = document.createElement('ul');

    this.wrapper.classList = 'dd-select__wrapper';

    if (this.config.isMultiselect) {
      this.wrapper.classList.add('dd-select__wrapper--multiselect');
    }

    if (this.config.isHighlightSelected) {
      this.wrapper.classList.add('dd-select__wrapper--highlight');
    }

    if (this.config.isBig) {
      this.wrapper.classList.add('dd-select__wrapper--big');
    }

    if (this.config.isDisabled) {
      this.wrapper.classList.add('dd-select__wrapper--disabled');
      this.root.setAttribute('disabled', true);
    }

    if (this.config.isOpen) {
      this.wrapper.classList.add('open');
    }

    this.inputWrapper.classList = 'dd-select__input';
    this.list.classList = 'dd-select__options';

    this.root.parentNode.replaceChild(this.wrapper, this.root);

    let item;
    this.options.forEach((option) => {
      if (option.value.length) {
        item = document.createElement('li');
        item.classList = 'dd-select__option';
        item.textContent = option.label;
        item.setAttribute('data-value', option.value);
        item.addEventListener('click', () => this.selectOption(event, option));

        if (this.config.hasDelete) {
          const btnDelete = document.createElement('button');
          btnDelete.classList = 'dd-select__option-delete dd-icon-bin';
          btnDelete.addEventListener('click', () =>
            this.deleteOption(event, option),
          );

          item.appendChild(btnDelete);
        }

        if (option.selected) {
          item.classList.add('selected');
        }

        this.list.appendChild(item);
      }
    });

    this.wrapper.appendChild(this.root);
    this.wrapper.appendChild(this.inputWrapper);
    this.wrapper.appendChild(this.list);
  }

  initEmptyInput() {
    const emptyInput = this.options.find((o) => !o.value.length);

    if (!emptyInput) {
      this.placeholder = null;
    } else {
      this.placeholder = document.createElement('span');
      this.placeholder.classList = 'dd-select__placeholder';
      this.placeholder.textContent = emptyInput.label;
    }
  }

  initOptions() {
    this.options = Array.from(this.root.options).map((option) => ({
      value: option.value,
      label: option.textContent,
      selected: option.getAttribute('selected') !== null,
    }));

    if (this.config.hasAll) {
      this.options.unshift({
        value: 'all',
        label: 'Alle',
        selected: this.options.every((o) => o.selected),
      });
    }
  }

  updateInputWrapper() {
    this.inputWrapper.innerHTML = '';

    if (this.config.isMultiselect) {
      const filteredOptions = this.options.filter(
        (o) => o.selected && o.value.length,
      );
      const allOption = this.options.find(
        (o) => o.value === 'all' && o.selected,
      );

      if (allOption) {
        const btnRemove = document.createElement('button');
        btnRemove.classList = 'dd-select__remove dd-icon-close-big';
        btnRemove.addEventListener('click', () => this.removeAllOptions);

        this.inputWrapper.appendChild(btnRemove);
        this.inputWrapper.appendChild(this.getTagFromOption(allOption));
      } else if (!filteredOptions.length && this.placeholder) {
        this.inputWrapper.appendChild(this.placeholder);
      } else {
        const btnRemove = document.createElement('button');
        btnRemove.classList = 'dd-select__remove dd-icon-close-big';
        btnRemove.addEventListener('click', () => this.removeAllOptions);

        this.inputWrapper.appendChild(btnRemove);

        filteredOptions.forEach((option, index) => {
          this.inputWrapper.appendChild(
            this.getTagFromOption(option, index !== 0),
          );
        });
      }
    } else {
      const selectedOption = this.options.find((o) => o.selected);
      const tag = this.getTagFromOption(
        selectedOption || this.options[0],
        false,
      );

      this.inputWrapper.appendChild(tag);
    }
  }

  getTagFromOption(option, hasComma = false) {
    const tag = document.createElement('span');
    tag.classList = 'dd-select__input-tag';
    tag.textContent = hasComma ? `, ${option.label}` : option.label;

    return tag;
  }

  updateList() {
    Array.from(this.list.children).forEach((item) => {
      item.classList.toggle(
        'selected',
        this.options.some((o) => o.selected && o.value === item.dataset.value),
      );
    });
  }

  updateRoot() {
    if (this.config.isMultiselect) {
      // When no options are selected, the empty options should be selected
      if (!this.options.find((o) => o.selected)) {
        this.options.find((o) => !o.value.length).selected = true;
      }

      Array.from(this.root.children).forEach((item) => {
        if (this.options.some((o) => o.selected && o.value === item.value)) {
          item.setAttribute('selected', 'selected');
        } else {
          item.removeAttribute('selected');
        }
      });
    } else {
      const selectedOption = this.options.find((o) => o.selected);

      Array.from(this.root.children).forEach((item) => {
        if (selectedOption.value === item.value) {
          item.setAttribute('selected', 'selected');
        } else {
          item.removeAttribute('selected');
        }
      });
    }
  }

  initEvents() {
    this.inputWrapper.addEventListener('click', (e) => {
      e.stopPropagation();

      if (e.target.classList.contains('dd-select__remove')) {
        this.removeAllOptions();
      } else {
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.closeDropdown();
        }
      }
    });

    window.addEventListener('click', () => {
      this.closeDropdown();
    });

    window.addEventListener('keydown', (e) => {
      if (e.key.toLowerCase() === 'escape') {
        this.closeDropdown();
      }
    });
  }

  openDropdown() {
    this.isOpen = true;
    this.wrapper.classList.add('open');
  }

  closeDropdown() {
    this.isOpen = false;
    this.wrapper.classList.remove('open');
  }

  selectOption(e, option) {
    e.stopPropagation();

    const emptyOption = this.options.find((o) => !o.value.length);
    if (emptyOption) {
      emptyOption.selected = false;
    }

    if (this.config.isMultiselect) {
      option.selected = !option.selected;

      if (this.config.hasAll) {
        if (option.value === 'all') {
          this.options.forEach((o) => {
            o.selected = option.selected;
          });
        } else {
          const optionsCopy = this.options.filter(
            (o) => !!o.value.length && o.value !== 'all',
          );

          this.options[0].selected = optionsCopy.every((o) => o.selected);
        }
      }
    } else {
      if (!option.selected) {
        const current = this.options.find((o) => o.selected);

        if (current) {
          current.selected = false;
        }

        option.selected = true;
      }

      this.closeDropdown();
    }

    this.update();
    this.root.dispatchEvent(new Event('change'));
  }

  deleteOption(e, option) {
    e.stopPropagation();

    console.info('multiselect.js: delete option ', option);
  }

  removeAllOptions() {
    this.options = this.options.map((o) => {
      o.selected = false;

      return o;
    });

    this.update();
  }

  update() {
    this.updateInputWrapper();
    this.updateList();
    this.updateRoot();
  }
}
